import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  RadioButtonUnchecked,
  Inventory,
  LocalShipping,
  Storefront,
  CheckCircle,
  Cancel,
  AssignmentReturn,
  HourglassEmpty,
  ErrorOutline,
  HelpOutline,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { GetPackageDetailByTracking } from "api/public";
import TrackingDetail from "components/TrackingDetail";

const normalizeStatus = (s) => {
  const lower = (s || "").toLowerCase();
  return lower === "end" ? "delivered" : lower;
};

const displayStatus = (status) => {
  if (["Pending", "Inprogress", "In-progress", "In-transit"].includes(status)) return "In-Transit";
  return status || "Unknown";
};

const getStatusConfig = (parcelStatus, secondaryColor) => {
  const STATUS_CONFIG = {
    started:       { Icon: Inventory,        color: "#78909C" },
    "in-progress": { Icon: LocalShipping,    color: "#1976D2" },
    "in-transit":  { Icon: LocalShipping,    color: "#1976D2" },
    arrived:       { Icon: Storefront,       color: secondaryColor || "#9C27B0" },
    delivered:     { Icon: CheckCircle,      color: "#2E7D32" },
    cancelled:     { Icon: Cancel,           color: "#C62828" },
    returned:      { Icon: AssignmentReturn, color: "#E65100" },
    "on hold":     { Icon: HourglassEmpty,   color: "#F57F17" },
    pending:       { Icon: HourglassEmpty,   color: "#F57F17" },
    lost:          { Icon: ErrorOutline,     color: "#B71C1C" },
  };
  return STATUS_CONFIG[normalizeStatus(parcelStatus)] || { Icon: HelpOutline, color: "#9E9E9E" };
};

const TrackingSummaryItem = ({ trackingId, masked = false }) => {
  const theme = useTheme();
  const secondary = theme.palette.secondary.main;

  const [expanded, setExpanded] = useState(false);
  const [shouldMount, setShouldMount] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    GetPackageDetailByTracking(trackingId, masked)
      .then((res) => {
        if (res.status === 200) {
          setSummary({ found: true, station: res.data.station, parcelStatus: res.data.status });
        } else {
          setSummary({ found: false });
        }
      })
      .catch(() => setSummary({ found: false }))
      .finally(() => setLoading(false));
  }, [trackingId, masked]);

  const handleToggle = () => {
    if (isPreloading) return;
    if (expanded) {
      setExpanded(false);
    } else if (shouldMount) {
      setExpanded(true);
    } else {
      setShouldMount(true);
      setIsPreloading(true);
    }
  };

  const handleReady = () => {
    setIsPreloading(false);
    setExpanded(true);
  };

  return (
    <Box>
      {/* Compact header row */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        px={2}
        py={1.25}
        sx={{
          cursor: isPreloading ? "default" : "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: isPreloading ? "transparent" : `${secondary}08` },
          transition: "background 0.15s",
        }}
        onClick={handleToggle}
      >
        {loading ? (
          <CircularProgress size={12} thickness={5} />
        ) : summary?.found ? (
          (() => {
            const { Icon, color } = getStatusConfig(summary.parcelStatus, secondary);
            return <Icon sx={{ fontSize: 18, color, flexShrink: 0 }} />;
          })()
        ) : (
          <RadioButtonUnchecked sx={{ fontSize: 18, color: theme.palette.error.main, flexShrink: 0 }} />
        )}

        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontFamily: "monospace" }}>
          {trackingId}
        </Typography>

        {!loading && summary?.found && (() => {
          const { color } = getStatusConfig(summary.parcelStatus, secondary);
          return (
            <Chip
              label={displayStatus(summary.parcelStatus) || summary.station}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22, borderColor: color, color }}
            />
          );
        })()}
        {!loading && !summary?.found && (
          <Chip label="Not found" size="small" variant="outlined" color="error" sx={{ fontSize: "0.7rem", height: 22 }} />
        )}

        {isPreloading ? (
          <CircularProgress size={14} thickness={4} sx={{ flexShrink: 0 }} />
        ) : expanded ? (
          <ExpandLess sx={{ fontSize: 18, color: "text.secondary" }} />
        ) : (
          <ExpandMore sx={{ fontSize: 18, color: "text.secondary" }} />
        )}
      </Box>

      {/* Expanded detail — mounts silently first, Collapse opens after onReady */}
      <Collapse in={expanded} timeout={300}>
        <Divider />
        <Box
          sx={{
            opacity: expanded ? 1 : 0,
            transition: "opacity 300ms ease",
            "& > div > .MuiPaper-root": {
              maxWidth: "100%",
              borderRadius: 0,
              boxShadow: "none",
              backgroundColor: theme.palette.grey[200],
              mt: 0,
            },
          }}
        >
          {shouldMount && (
            <TrackingDetail trackingNumber={trackingId} masked={masked} onReady={handleReady} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default TrackingSummaryItem;
